import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MatchesList from '../components/MatchesList';
import Seo from '../components/Seo';
import { breadcrumbSchema } from '../utils/structuredData';

import '../styles/Matches.css';
import { formatDateToISO } from '../utils/formatters';

// Import MUI components for the DatePicker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const UpcomingMatches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem('upcomingMatchesDate');
    return savedDate ? new Date(savedDate) : null;
  });

  useEffect(() => {
    const loadMatches = async () => {
      setIsLoading(true);
      try {
        const date = formatDateToISO(selectedDate);
        const url = date
          ? `/api/rl/public/upcoming-matches?date=${encodeURIComponent(date)}`
          : '/api/rl/public/upcoming-matches';
        const res = await fetch(url);
        const upcomingMatches = await res.json();
        setMatches(upcomingMatches);
        setFilteredMatches(upcomingMatches);
      } catch (error) {
        console.error("Error loading upcoming matches:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMatches();
  }, [selectedDate]);

  // Combined filtering logic: filters by league and by selected date
  const applyFilters = (leagueFilter) => {
    let filtered = matches;

    // Filter by league if a specific filter is set
    if (leagueFilter !== 'all') {
      filtered = filtered.filter(match =>
        match.league.toLowerCase().includes(leagueFilter.toLowerCase())
      );
    }

    setFilteredMatches(filtered);
  };

  // Handle filter changes for league buttons
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    applyFilters(filter);
  };

  // Handle date change from the DatePicker
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      localStorage.setItem('upcomingMatchesDate', date.toISOString());
    } else {
      localStorage.removeItem('upcomingMatchesDate');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Seo
        title="Upcoming Rocket League Matches — Schedule & Times"
        description="The full schedule of upcoming Rocket League esports matches — RLCS, regionals, and majors. Dates, times, and matchups so you never miss a game."
        path="/upcoming-matches"
        schemas={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Upcoming Matches', path: '/upcoming-matches' }])]}
      />
      <div className="page-header">
        <button onClick={handleBack} className="back-link">← Back</button>
        <h1>Upcoming Matches</h1>
      </div>

      <div className="matches-filters">
        <button
          className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        <button
          className={`filter-btn ${activeFilter === 'rlcs' ? 'active' : ''}`}
          onClick={() => handleFilterChange('rlcs')}
        >
          RLCS
        </button>
        <button
          className={`filter-btn ${activeFilter === 'regional' ? 'active' : ''}`}
          onClick={() => handleFilterChange('regional')}
        >
          Regional
        </button>
        <button
          className={`filter-btn ${activeFilter === 'major' ? 'active' : ''}`}
          onClick={() => handleFilterChange('major')}
        >
          Major
        </button>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="date-picker-container">
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: { size: 'small', label: '', placeholder: 'Filter by date' },
                // Tags the portaled popper so admin stylesheets can't repaint it
                popper: { className: 'jolt-calendar' },
              }}
            />
            {selectedDate && (
              <button
                className="reset-date-btn"
                onClick={() => {
                  setSelectedDate(null);
                  localStorage.removeItem('upcomingMatchesDate');
                }}
              >
                ×
              </button>
            )}
          </div>
        </LocalizationProvider>
      </div>

      <MatchesList
        matches={filteredMatches}
        isCompleted={false}
        isLoading={isLoading}
        matchDate={formatDateToISO(selectedDate)}
      />
    </>
  );
};

export default UpcomingMatches;
